/**
 * Chart Export Utilities
 * Provides offline export functionality for Highcharts charts
 * Compatible with Highcharts 9.1.2
 */

import { shortDate } from '@/helpers/dateFormatter';
import { intToTimeStr } from '@/helpers/dateTime';
import { track } from '@/modules/Flexigo/helpers/rum';

// `surface`: a fixed name for the chart, so Datadog RUM can tell exports apart (`export:png:otp`).
// Never the filename, which carries dates.

/**
 * Export chart as PNG image
 * @param {Object} chart - Highcharts chart instance
 * @param {string} filename - Filename without extension
 * @param {Object} options - Export options (width, height, scale, surface)
 */
export function exportChartAsPNG(chart, filename = 'chart', options = {}) {
  if (!chart) {
    console.error('Chart instance not found');
    return;
  }

  const { width = 1200, height = 600, scale = 2, surface = 'chart' } = options;
  track(`export:png:${surface}`);

  // Build export options with fixed axis scales (if chart has yAxis)
  const exportOptions = {
    chart: {
      width,
      height,
    },
  };

  // Capture current axis extremes to maintain consistent scales (only for real Highcharts instances)
  if (chart.yAxis && Array.isArray(chart.yAxis)) {
    const yAxisExtremes = chart.yAxis.map((axis) => ({
      min: axis.min,
      max: axis.max,
    }));

    exportOptions.yAxis = yAxisExtremes.map((extremes) => ({
      min: extremes.min,
      max: extremes.max,
    }));
  }

  // Get SVG with specified dimensions and fixed scales
  const svg = chart.getSVG(exportOptions);

  // Create canvas and convert SVG to PNG with scaling
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();

  img.onload = () => {
    // Apply scale for higher resolution
    canvas.width = width * scale;
    canvas.height = height * scale;

    // Scale context for high DPI
    ctx.scale(scale, scale);

    // Fill background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Draw image
    ctx.drawImage(img, 0, 0, width, height);

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  };

  img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

/**
 * Export chart as PDF, rendered client-side by the offline-exporting module.
 *
 * That module has to be registered by whoever owns the chart (Highcharts is a singleton, so
 * one registration covers the page), otherwise `exportChartLocal` is not on the instance.
 *
 * @param {Object} chart - Highcharts chart instance
 * @param {string} filename - Filename without extension
 */
export function exportChartAsPDF(chart, filename = 'chart', surface = 'chart') {
  if (!chart?.exportChartLocal) {
    console.error('Chart instance cannot export locally');
    return;
  }
  track(`export:pdf:${surface}`);

  chart.exportChartLocal({ type: 'application/pdf', filename });
}

/**
 * Open the browser's print dialog for a chart.
 *
 * @param {Object} chart - Highcharts chart instance
 */
export function printChart(chart, surface = 'chart') {
  if (!chart?.print) {
    console.error('Chart instance not found');
    return;
  }
  track(`export:print:${surface}`);

  chart.print();
}

/**
 * Export data as CSV
 * @param {Array} headers - Array of column headers
 * @param {Array} rows - Array of row data arrays
 * @param {string} filename - Filename without extension
 */
export function exportAsCSV(headers, rows, filename = 'data', surface = 'chart') {
  if (!headers || !rows || rows.length === 0) {
    console.error('Invalid data for CSV export');
    return;
  }
  track(`export:csv:${surface}`);

  const csvData = [];
  csvData.push(headers.join(','));

  rows.forEach((row) => {
    csvData.push(row.join(','));
  });

  const csvContent = csvData.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

/**
 * Export data as Excel (HTML table format)
 * @param {Array} headers - Array of column headers
 * @param {Array} rows - Array of row data arrays
 * @param {string} filename - Filename without extension
 */
export function exportAsExcel(headers, rows, filename = 'data', surface = 'chart') {
  if (!headers || !rows || rows.length === 0) {
    console.error('Invalid data for Excel export');
    return;
  }
  track(`export:excel:${surface}`);

  let html = '<table>';
  html += '<thead><tr>';
  headers.forEach((header) => {
    html += `<th>${header}</th>`;
  });
  html += '</tr></thead><tbody>';

  rows.forEach((row) => {
    html += '<tr>';
    row.forEach((cell) => {
      html += `<td>${cell}</td>`;
    });
    html += '</tr>';
  });

  html += '</tbody></table>';

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.xls`;
  link.click();
  URL.revokeObjectURL(link.href);
}

/**
 * The by-date chart bucket a CSV row stands for: the day, the day and hour, or the selected days
 * a week or month covers. Kept free of commas, since exportAsCSV does not quote its cells.
 * @param {Object} bucket - Bucket with period, date, hour, start and end
 * @returns {string} Bucket label
 */
function exportBucketLabel({ period, date, hour, start, end }) {
  if (period === 'hourly') return `${shortDate(date, '')} ${intToTimeStr(hour * 100)}`;
  if ((period === 'weekly' || period === 'monthly') && start !== end) {
    return `${shortDate(start, '')} - ${shortDate(end, '')}`;
  }
  return shortDate(start ?? date, String(date ?? ''));
}

/**
 * Prepare OTP data for export
 * @param {Array} lateArrivals - Late arrivals data
 * @param {Array} earlyDepartures - Early departures data
 * @param {Array} otpPercentages - OTP percentages data
 * @returns {Object} Object with headers and rows
 */
export function prepareOTPDataForExport(lateArrivals, earlyDepartures, otpPercentages) {
  const headers = [
    'Date',
    'Late Min',
    'Late Q1',
    'Late Median',
    'Late Q3',
    'Late Max',
    'Early Min',
    'Early Q1',
    'Early Median',
    'Early Q3',
    'Early Max',
    'OTP %',
  ];

  const rows = [];
  // Helper to render null/undefined as empty string (empty days have no values)
  const cell = (value) => (value === null || value === undefined ? '' : value);

  lateArrivals.forEach((late, index) => {
    const early = earlyDepartures[index];
    const otp = otpPercentages[index];
    const row = [
      exportBucketLabel(late),
      cell(late.min),
      cell(late.q1),
      cell(late.median),
      cell(late.q3),
      cell(late.max),
      cell(early.min),
      cell(early.q1),
      cell(early.median),
      cell(early.q3),
      cell(early.max),
      cell(otp.percentage),
    ];
    rows.push(row);
  });

  return { headers, rows };
}

/**
 * Generate SVG for stacked bar charts from route data
 * @param {Array} routes - Array of route objects with name and stats
 * @param {Object} options - Configuration options
 * @returns {string} SVG string
 */
export function generateStackedBarChartSVG(routes, options = {}) {
  const {
    width = 800,
    barHeight = 40,
    rowHeight = 60,
    labelWidth = 200,
    chartWidth = 500,
    padding = 20,
    colors = {
      late: '#ff4059',
      onTime: '#687692',
      early: '#5287e3',
      skipped: '#97a0b4',
    },
  } = options;

  const height = routes.length * rowHeight + padding * 2;

  // Helper function to escape XML special characters
  const escapeXml = (text) =>
    String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

  // Generate SVG for stacked bar charts
  let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <rect width="${width}" height="${height}" fill="#ffffff"/>`;

  routes.forEach((route, index) => {
    const y = padding + index * rowHeight;
    const { stats } = route;

    // Route name
    svgContent += `<text x="${padding}" y="${
      y + 25
    }" font-family="Arial" font-size="14" fill="#000000" text-anchor="start">${escapeXml(route.name)}</text>`;

    // Stacked bar chart
    const barX = padding + labelWidth;
    const barY = y + 5;

    let currentX = barX;
    const segments = [
      { value: stats.late, color: colors.late },
      { value: stats.onTime, color: colors.onTime },
      { value: stats.early, color: colors.early },
      { value: stats.skipped, color: colors.skipped },
    ];

    segments.forEach((segment) => {
      if (segment.value > 0) {
        const segmentWidth = (segment.value / 100) * chartWidth;
        svgContent += `<rect x="${currentX}" y="${barY}" width="${segmentWidth}" height="${barHeight}" fill="${segment.color}"/>`;

        // Add percentage label if segment is large enough
        if (segment.value > 5) {
          svgContent += `<text x="${currentX + segmentWidth / 2}" y="${
            barY + barHeight / 2 + 5
          }" font-family="Arial" font-size="12" fill="#ffffff" text-anchor="middle">${segment.value}%</text>`;
        }

        currentX += segmentWidth;
      }
    });
  });

  svgContent += '</svg>';
  return svgContent;
}
